/**
 * WLSN Modal Component
 * 
 * Global modal system with two operating modes:
 * 
 * MOVE MODE (default - data-modal-mode="move"):
 * - Physically moves the modal content element into the modal overlay
 * - When closed, moves content back to its original position in the DOM
 * - Preserves ALL event listeners, element state, and associated data
 * - Use this when your content has interactive elements with bound events (forms, buttons, custom components)
 * - Example: <wlsn-modal id="my-modal" data-modal-mode="move">...</wlsn-modal>
 * 
 * TEMPLATE MODE (data-modal-mode="template"):
 * - Clones the modal content on first open and reuses that clone
 * - Original content remains in its original position (hidden)
 * - Better performance for modals that are opened/closed frequently
 * - Use this for static or read-only content (text, images, etc.)
 * - Example: <wlsn-modal id="my-modal" data-modal-mode="template">...</wlsn-modal>
 * 
 * HTML Structure:
 * <wlsn-modal id="unique-id" data-modal-mode="move">
 *   <div class="wlsn-modal__container js-wlsn-modal-container">
 *     <!-- Your modal content here -->
 *     <button class="js-close-modal">Close</button>
 *   </div>
 *   <div class="wlsn-modal__overlay js-wlsn-modal-overlay"><span></span></div>
 * </wlsn-modal>
 * 
 * Note: Container and overlay are siblings. Container is above overlay in DOM (and z-index).
 * Clicking overlay closes the modal, but events don't interfere with container content.
 * 
 * Trigger: <button data-modal-trigger="unique-id">Open</button>
 * 
 * Features:
 * - Close on overlay click, ESC key, or close button (js-close-modal class)
 * - Body scroll lock when modal is open
 * - Accessibility support with ARIA attributes
 * - Custom events: wlsn:modal:open and wlsn:modal:close
 * - Support for multiple modals (one open at a time, extensible for stacking)
 * - Auto-play video: Add data-play-video-on-open attribute to automatically play wlsn-video elements when modal opens
 *   Example: <wlsn-modal id="video-modal" data-play-video-on-open>...</wlsn-modal>
 */

if (!customElements.get('wlsn-modal')) {
  class WLSNModal extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        // JS classes for targeting elements
        jsOverlay: 'js-wlsn-modal-overlay',
        jsContainer: 'js-wlsn-modal-container',
        jsCloseButton: 'js-close-modal',
        
        // CSS classes for styling states
        active: 'active',
        lockScroll: 'wlsn-lock-scroll',
      };

      this.isOpen = false;
      this.mode = this.dataset.modalMode || 'move';
      this.modalId = this.id;
      
      // Storage for move mode
      this.originalParent = null;
      this.originalNextSibling = null;
      
      // Storage for template mode
      this.clonedContent = null;
      this.originalModalContent = null;
      
      // Modal overlay and container (from HTML structure)
      this.overlay = null;
      this.container = null;

      // Bound methods
      this.boundCloseModal = this.closeModal.bind(this);
      this.boundHandleEscKey = this._handleEscKey.bind(this);
      this.boundHandleOverlayClick = this._handleOverlayClick.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        container: this.querySelector('.js-wlsn-modal-container'),
        overlay: this.querySelector(`.${this.classes.jsOverlay}`),
        closeButtons: this.querySelectorAll(`.${this.classes.jsCloseButton}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (!this.elements.overlay || !this.elements.container) {
        console.error('WLSN Modal: Missing required structure. Please include container and overlay divs with js-wlsn-modal-container and js-wlsn-modal-overlay classes.');
        return;
      }

      // Store original modal content for template mode
      if (this.mode === 'template') {
        this.originalModalContent = this.elements.container.cloneNode(true);
      }

      // Set ARIA attributes
      this.elements.overlay.setAttribute('aria-hidden', 'true');
      this.elements.container.setAttribute('role', 'dialog');
      this.elements.container.setAttribute('aria-modal', 'true');
      this.elements.container.setAttribute('aria-labelledby', this.modalId);
      
      // Add overlay click listener (overlay is now a sibling, behind container)
      this.elements.overlay.addEventListener('click', this.boundHandleOverlayClick);
      
      // Register this modal in the global registry
      WLSNModal.registerModal(this);
      
      // Set up triggers
      this._setupTriggers();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      // Clean up
      WLSNModal.unregisterModal(this);
      this._removeTriggers();
      
      if (this.elements.overlay) {
        this.elements.overlay.removeEventListener('click', this.boundHandleOverlayClick);
      }
    }

    /**
     * Setup trigger elements for opening the modal
     * @returns {void}
     */
    _setupTriggers() {
      // Find all triggers for this modal
      this.triggers = document.querySelectorAll(`[data-modal-trigger="${this.modalId}"]`);
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', this.boundOpenModal = this._openModal.bind(this));
      });
    }

    /**
     * Remove trigger event listeners
     * @returns {void}
     */
    _removeTriggers() {
      if (this.triggers) {
        this.triggers.forEach(trigger => {
          trigger.removeEventListener('click', this.boundOpenModal);
        });
      }
    }

    /**
     * Open the modal (private method, triggered by buttons)
     * @param {Event} event - The click event
     * @returns {void}
     */
    _openModal(event) {
      if (event) {
        event.preventDefault();
      }

      // Close any other open modals
      WLSNModal.closeAllModals();

      // Handle content based on mode
      if (this.mode === 'move') {
        this._handleMoveMode();
      } else if (this.mode === 'template') {
        this._handleTemplateMode();
      }

      // Show modal
      this._showModal();
    }

    /**
     * Open the modal programmatically (public API)
     * Use this when opening without a trigger click (e.g. newsletter popup after delay)
     * @returns {void}
     */
    openModal() {
      this._openModal(null);
    }

    /**
     * Handle move mode - physically move modal to body
     * @returns {void}
     */
    _handleMoveMode() {
      // In move mode, content is already in container
      // We just need to track the original parent for the entire modal element
      this.originalParent = this.parentNode;
      this.originalNextSibling = this.nextSibling;
      
      // Move entire modal to body
      document.body.appendChild(this);
    }

    /**
     * Handle template mode - clone and restore content
     * @returns {void}
     */
    _handleTemplateMode() {
      // In template mode, restore original content each time
      if (this.originalModalContent) {
        // Clear current container
        this.elements.container.innerHTML = '';
        
        // Clone and append original content
        const clone = this.originalModalContent.cloneNode(true);
        while (clone.firstChild) {
          this.elements.container.appendChild(clone.firstChild);
        }
        
        // Upgrade all custom elements in the cloned content
        // This ensures that connectedCallback() is called on all custom elements
        customElements.upgrade(this.elements.container);
      }
      
      // Move entire modal to body
      if (this.parentNode !== document.body) {
        this.originalParent = this.parentNode;
        this.originalNextSibling = this.nextSibling;
        document.body.appendChild(this);
      }
    }

    /**
     * Show the modal with animations
     * @returns {void}
     */
    _showModal() {
      // Show overlay and container with animation
      requestAnimationFrame(() => {
        this.elements.overlay.classList.add(this.classes.active);
        this.elements.container.classList.add(this.classes.active);
      });

      // Lock body scroll
      document.body.classList.add(this.classes.lockScroll);

      // Add ESC key listener
      document.addEventListener('keydown', this.boundHandleEscKey);

      // Set up close buttons
      this._setupCloseButtons();

      // Update state
      this.isOpen = true;
      this.elements.overlay.setAttribute('aria-hidden', 'false');

      // Auto-play video if data-play-video-on-open attribute is present
      if (this.hasAttribute('data-play-video-on-open')) {
        this._playVideoOnOpen();
      }

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('wlsn:modal:open', {
        bubbles: true,
        detail: { modalId: this.modalId, mode: this.mode }
      }));
    }

    /**
     * Close the modal (can be called externally)
     * @returns {void}
     */
    closeModal() {
      if (!this.isOpen) return;

      // Remove animations
      this.elements.overlay.classList.remove(this.classes.active);
      this.elements.container.classList.remove(this.classes.active);

      // Wait for animation to complete before cleanup
      setTimeout(() => {
        this._cleanupModal();
      }, 300); // Match CSS transition duration

      // Unlock body scroll
      document.body.classList.remove(this.classes.lockScroll);

      // Remove ESC key listener
      document.removeEventListener('keydown', this.boundHandleEscKey);

      // Update state
      this.isOpen = false;
      this.elements.overlay.setAttribute('aria-hidden', 'true');

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('wlsn:modal:close', {
        bubbles: true,
        detail: { modalId: this.modalId, mode: this.mode }
      }));
    }

    /**
     * Cleanup modal after closing
     * @returns {void}
     */
    _cleanupModal() {
      // Move modal back to original position
      if (this.originalParent && this.originalParent.nodeType === Node.ELEMENT_NODE) {
        if (this.originalNextSibling && this.originalNextSibling.parentNode) {
          this.originalParent.insertBefore(this, this.originalNextSibling);
        } else {
          this.originalParent.appendChild(this);
        }
      }

      // Remove close button listeners
      this._removeCloseButtons();
    }

    /**
     * Setup close button event listeners
     * @returns {void}
     */
    _setupCloseButtons() {
      this.elements.closeButtons.forEach(button => {
        button.addEventListener('click', this.boundCloseModal);
      });
    }

    /**
     * Remove close button event listeners
     * @returns {void}
     */
    _removeCloseButtons() {
      if (this.elements.closeButtons) {
        this.elements.closeButtons.forEach(button => {
          button.removeEventListener('click', this.boundCloseModal);
        });
      }
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} event - The keyboard event
     * @returns {void}
     */
    _handleEscKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    }

    /**
     * Handle overlay click event
     * @param {Event} event - The click event
     * @returns {void}
     */
    _handleOverlayClick(event) {
      // Prevent event from passing through overlay to elements behind modal
      event.stopPropagation();
      event.preventDefault();
      
      // Close modal when overlay is clicked (overlay is now a sibling behind container)
      this.closeModal();
    }

    /**
     * Auto-play video when modal opens
     * @returns {void}
     */
    _playVideoOnOpen() {
      // Wait for next tick to ensure DOM is ready
      requestAnimationFrame(() => {
        const videoElement = this.elements.container.querySelector('wlsn-video');
        if (videoElement && typeof videoElement.videoPlay === 'function') {
          videoElement.videoPlay();
        }
      });
    }

    // Static registry for managing multiple modals
    static modals = new Map();

    /**
     * Register a modal in the global registry
     * @param {WLSNModal} modal - The modal instance to register
     * @returns {void}
     */
    static registerModal(modal) {
      WLSNModal.modals.set(modal.modalId, modal);
    }

    /**
     * Unregister a modal from the global registry
     * @param {WLSNModal} modal - The modal instance to unregister
     * @returns {void}
     */
    static unregisterModal(modal) {
      WLSNModal.modals.delete(modal.modalId);
    }

    /**
     * Close all open modals
     * @returns {void}
     */
    static closeAllModals() {
      WLSNModal.modals.forEach(modal => {
        if (modal.isOpen) {
          modal.closeModal();
        }
      });
    }

    /**
     * Get modal instance by ID
     * @param {string} modalId - The modal ID
     * @returns {WLSNModal|undefined} - The modal instance or undefined
     */
    static getModal(modalId) {
      return WLSNModal.modals.get(modalId);
    }
  }

  customElements.define('wlsn-modal', WLSNModal);
}

